import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { isFuture } from 'date-fns';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import Autocomplete from '@components/Autocomplete';
import SplitButton from '@components/SplitButton';
import Tooltip from '@components/Tooltip';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { CAMPAIGN_STATUS, POST_CHANNEL, isStoryPlatform } from '@constants/social-posts';
import { PLACEHOLDER_CONTEXT, getPlaceholderTokensForContext } from '@constants/placeholders';
import { alertActions, socialPostsActions } from '@actions';
import { socialPostsService } from '@services';
import { ModalType } from '@constants/modal';
import { handleApiError } from '@helpers/api';
import { useSocialPostsPlatforms, useLocations } from '@hooks';
import { isLocationDirectForPlatform } from '@hooks/useSocialPostsPlatforms';
import { StyledTimeframePicker, FormWrapper, Modal, PreviewWrapper, TwoColumnRow } from './styles';
import PostPreview from './PostPreview';
import ContentTabs from './ContentTabs';
import {
    testBaseMessageLength,
    getPlatformIssues,
    isOverrideMessageMissing,
    fillMissingOverrideMessages,
    resolveOverrideMessage,
    resolveOverrideMedia,
} from './platformRules';
import {
    useSavedVideoDurations,
    withMeasuredDurations,
    overridesWithMeasuredDurations,
} from './useSavedVideoDurations';
import {
    defaultTikTokOptions,
    normalizeTikTokOptions,
    deriveTikTokConstraints,
    getTikTokIssues,
    applyCreatorRestrictions,
    blocksDraft,
    TIKTOK_ISSUE,
} from './tiktokRules';
import { useTikTokCreatorInfo } from './useTikTokCreatorInfo';

const POST_PLACEHOLDER_TOKENS = getPlaceholderTokensForContext(PLACEHOLDER_CONTEXT.POST);

const EditModal = ({
    modal,
    setModal,
    selectedSocialPost,
    setSelectedSocialPost,
    refetchAvailablePlatforms,
    initialScheduledTime,
    refetchPosts,
    onDelete,
}) => {
    const { t } = useTranslation();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMediaUploading, setIsMediaUploading] = useState(false);
    const { data } = useSelector(state => state.socialPosts);
    const dispatch = useDispatch();
    const { getAllPlatformsOptions, getAccountPlatformsOptions } = useSocialPostsPlatforms();
    const {
        mapSelectedLocationsToFormValue,
        getLocationsOptions,
        locationsMap,
        getWritableLocationsOptions,
        hasSingleLocation,
        singleLocationId,
        singleLocationFormValue,
    } = useLocations();

    // Completed campaigns are immutable, so they only ever open in view mode (the table hides
    // their edit action) — there's no editable published state to special-case here.
    const isView = modal.type === ModalType.View;

    const canDelete =
        modal.type !== ModalType.Create &&
        !!selectedSocialPost?.canDelete &&
        selectedSocialPost?.status !== CAMPAIGN_STATUS.COMPLETED;

    const buttonOptions = [
        { value: 0, label: t('common.none') },
        { value: 1, label: t('SocialPosts.book') },
        { value: 2, label: t('SocialPosts.orderOnline') },
        { value: 3, label: t('SocialPosts.buy') },
        { value: 4, label: t('common.learnMore') },
        { value: 5, label: t('SocialPosts.signUp') },
        { value: 6, label: t('SocialPosts.callNow') },
    ];

    const SocialPostSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        message: yup
            .string()
            .test('effective-message-required', 'validation.required', function (value) {
                if (value) return true;
                const { platforms, overrides } = this.parent;
                if (!platforms?.length) return false;
                const textPlatforms = platforms.filter(p => !isStoryPlatform(p));
                if (!textPlatforms.length) return true;
                return textPlatforms.every(p => (overrides || []).some(o => o.platform === p));
            })
            .test('text-editor-length', 'validation.maxLength', function (value) {
                const { platforms } = this.parent;
                if (platforms?.length && platforms.every(isStoryPlatform)) return true;
                return testBaseMessageLength(platforms, value || '', POST_PLACEHOLDER_TOKENS);
            }),
        platforms: yup
            .array()
            .min(1, 'validation.required')
            .test(
                'platforms-have-direct-companies',
                'validation.platformWithoutDirectCompany',
                function (selectedPlatforms) {
                    if (isView) return true;
                    const { companies } = this.parent;
                    if (!selectedPlatforms?.length || !companies?.length) return true;
                    return selectedPlatforms.every(platform =>
                        companies.some(c => isLocationDirectForPlatform(locationsMap[c.id], platform)),
                    );
                },
            ),
        companies: yup.array().min(1, 'validation.required'),
        scheduledTime: isView
            ? yup.string()
            : yup
                  .string()
                  .nullable()
                  .required('validation.required')
                  .test('test-future-date', '', value => isFuture(new Date(value))),
        button: yup.number(),
        buttonContent: yup.string().when('button', {
            is: buttonValue => buttonValue !== 0 && buttonValue !== 6,
            then: schema => schema.url('validation.invalidURL').required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        media: yup.array().test('instagram-effective-media-required', 'validation.required', function (value) {
            // Instagram requires media. Validate the resolved media (override wins, incl. an
            // explicit empty array; null inherits the base).
            const { platforms, overrides } = this.parent;
            if (!platforms?.includes(POST_CHANNEL.INSTAGRAM)) return true;
            const igOverride = (overrides || []).find(o => o.platform === POST_CHANNEL.INSTAGRAM);
            return resolveOverrideMedia(igOverride, value).length > 0;
        }),
    });

    const resolver = useYupValidationResolver(SocialPostSchema);

    const {
        getValues,
        setValue,
        trigger,
        control,
        reset,
        watch,
        formState: { isValid, isDirty, errors },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            message: '',
            platforms: [],
            companies: [],
            scheduledTime: '',
            button: 0,
            buttonContent: '',
            autoAddMusic: false,
            tikTokOptions: defaultTikTokOptions(),
            media: [],
            overrides: [],
        },
    });

    const selectedCompanies = watch('companies');
    const button = watch('button');
    const buttonContent = watch('buttonContent');
    const autoAddMusic = watch('autoAddMusic');
    const tikTokOptions = watch('tikTokOptions');
    const message = watch('message');
    const scheduledTime = watch('scheduledTime');
    const platforms = watch('platforms');
    const overrides = watch('overrides');

    // Saved posts may reference companies that no longer survive mapSelectedLocationsToFormValue
    // (deleted, permission-filtered, not yet loaded). For view/published preview, fall back
    // to a stub keyed by the raw id so the post still renders.
    const previewCompanies = useMemo(() => {
        if (!isView) return selectedCompanies;
        const rawIds = selectedSocialPost?.companies || [];
        if (!rawIds.length) return selectedCompanies;
        const mapped = new Map(selectedCompanies.map(c => [c.id, c]));
        return rawIds.map(id => mapped.get(id) || { id, label: '' });
    }, [isView, selectedCompanies, selectedSocialPost?.companies]);
    const googleIsSelected = watch('platforms')?.includes(POST_CHANNEL.GOOGLE);
    const tiktokIsSelected = watch('platforms')?.includes(POST_CHANNEL.TIKTOK);
    const media = watch('media');

    // Videos reloaded from a saved campaign have no duration — re-measure so per-platform rules
    // apply to them as well as to this session's uploads.
    // Only overrides for selected platforms — deselected ones are dropped at save and validated
    // nowhere, so their media must not delay the save gate.
    const { durations: savedVideoDurations, isMeasuring: isMeasuringVideos } = useSavedVideoDurations(
        media,
        ...(overrides || []).filter(o => platforms?.includes(o?.platform)).map(o => o?.media),
    );
    const measuredMedia = useMemo(
        () => withMeasuredDurations(media, savedVideoDurations),
        [media, savedVideoDurations],
    );
    const measuredOverrides = useMemo(
        () => overridesWithMeasuredDurations(overrides, savedVideoDurations),
        [overrides, savedVideoDurations],
    );

    const directProfilePredicate = useCallback(
        loc => platforms?.some(p => isLocationDirectForPlatform(loc, p)) ?? false,
        [platforms],
    );

    // `locationsMap` dep ensures the list refreshes when account data loads/changes mid-modal.
    const locationsOptions = useMemo(() => {
        if (isView) return getLocationsOptions(directProfilePredicate);
        if (!platforms?.length) return [];
        return getWritableLocationsOptions('posts', directProfilePredicate);
    }, [isView, platforms, locationsMap, directProfilePredicate]);

    const platformsOptions = isView ? getAllPlatformsOptions() : getAccountPlatformsOptions();

    // Prune only on platform removal — not on mount or load — so saved indirect/non-writable
    // locations aren't silently dropped when the user opens an existing post.
    const prevPlatformsRef = useRef(platforms);
    useEffect(() => {
        if (isView) return;
        const prev = prevPlatformsRef.current;
        prevPlatformsRef.current = platforms;
        const aPlatformWasRemoved = prev.some(p => !platforms.includes(p));
        if (!aPlatformWasRemoved) return;

        if (platforms.length === 0) {
            if (selectedCompanies.length > 0) setValue('companies', [], { shouldValidate: true });
            return;
        }
        const stillValid = selectedCompanies.filter(c => locationsOptions.some(o => o.id === c.id));
        if (stillValid.length !== selectedCompanies.length) {
            setValue('companies', stillValid, { shouldValidate: true });
        }
    }, [platforms, locationsOptions]);

    const autoSelectSingleLocation =
        hasSingleLocation && !isView && locationsOptions.some(o => o.id === singleLocationId);

    useEffect(() => {
        if (autoSelectSingleLocation) {
            setValue('companies', singleLocationFormValue, { shouldValidate: true });
        }
    }, [autoSelectSingleLocation, setValue, singleLocationFormValue]);

    useEffect(() => {
        const isNewSocialPost = modal.type === ModalType.Create;
        if (isNewSocialPost) {
            if (initialScheduledTime) {
                setValue('scheduledTime', initialScheduledTime, { shouldValidate: true });
            }
            return;
        }
        if (!isNewSocialPost) {
            setValue('title', selectedSocialPost?.title || '', { shouldValidate: true });
            setValue('message', selectedSocialPost?.message, { shouldValidate: true });
            setValue('platforms', selectedSocialPost?.platforms || [], {
                shouldValidate: true,
            });
            setValue('companies', mapSelectedLocationsToFormValue(selectedSocialPost?.companies), {
                shouldValidate: true,
            });
            setValue('scheduledTime', new Date(selectedSocialPost?.scheduledTime), {
                shouldValidate: true,
            });
            setValue('button', selectedSocialPost?.button);
            setValue('buttonContent', selectedSocialPost?.buttonContent || '');
            setValue('autoAddMusic', selectedSocialPost?.autoAddMusic || false);
            setValue('tikTokOptions', normalizeTikTokOptions(selectedSocialPost?.tikTokOptions));
            setValue('media', selectedSocialPost?.media || [], { shouldValidate: true });
            setValue(
                'overrides',
                fillMissingOverrideMessages(selectedSocialPost?.overrides || [], selectedSocialPost?.message),
                { shouldValidate: true },
            );
            trigger('message');
        }
    }, [selectedSocialPost, setValue, trigger, modal.type, initialScheduledTime]);

    const onCloseModal = () => {
        setModal({ open: false, type: ModalType.Create });
        setSelectedSocialPost({});
        reset();
    };

    // Gate save: block when any selected platform's resolved content breaks a platform rule.
    // Guardrail only — the counter discounts placeholders while the server measures the
    // substituted string, so the server pre-flight stays authoritative.
    const hasPlatformIssues = (platforms || []).some(platform => {
        const override = (measuredOverrides || []).find(o => o.platform === platform);
        return (
            isOverrideMessageMissing(override) ||
            getPlatformIssues(
                platform,
                resolveOverrideMessage(override, message),
                resolveOverrideMedia(override, measuredMedia),
                POST_PLACEHOLDER_TOKENS,
            ).length > 0
        );
    });

    const tiktokLocationIds = useMemo(
        () =>
            (selectedCompanies || [])
                .filter(c => isView || isLocationDirectForPlatform(locationsMap[c.id], POST_CHANNEL.TIKTOK))
                .map(c => c.id),
        [selectedCompanies, locationsMap, isView],
    );
    const tiktokCreatorInfo = useTikTokCreatorInfo({
        enabled: tiktokIsSelected && !isView,
        locationIds: tiktokLocationIds,
    });
    const unnamedCreator = t('SocialPosts.tiktok.thisAccount');
    const tiktokConstraints = useMemo(
        () => deriveTikTokConstraints(tiktokCreatorInfo.creators, tiktokCreatorInfo.isAudited, unnamedCreator),
        [tiktokCreatorInfo.creators, tiktokCreatorInfo.isAudited, unnamedCreator],
    );
    const tiktokIssues = useMemo(() => {
        if (!tiktokIsSelected || isView) return [];
        const override = (measuredOverrides || []).find(o => o.platform === POST_CHANNEL.TIKTOK);
        return getTikTokIssues({
            options: tikTokOptions,
            constraints: tiktokConstraints,
            media: resolveOverrideMedia(override, measuredMedia),
            status: tiktokCreatorInfo.status,
            creatorCount: tiktokCreatorInfo.creators.length,
            fallbackHandle: unnamedCreator,
        });
    }, [
        tiktokIsSelected,
        isView,
        measuredOverrides,
        measuredMedia,
        tikTokOptions,
        tiktokConstraints,
        tiktokCreatorInfo.status,
        tiktokCreatorInfo.creators.length,
        unnamedCreator,
    ]);

    // The checkbox renders unchecked for a creator-disabled interaction and the payload is
    // normalized on save, so no correcting write-back is needed here.
    const blockingTikTokIssues = useMemo(() => tiktokIssues.filter(i => i.blocking), [tiktokIssues]);
    const draftBlockingIssues = useMemo(() => blockingTikTokIssues.filter(blocksDraft), [blockingTikTokIssues]);
    const firstBlocker = issues => issues.find(i => i.code === TIKTOK_ISSUE.DISCLOSURE_INCOMPLETE) || issues[0] || null;

    const saveBlocked =
        isMediaUploading || isMeasuringVideos || isSubmitting || !isValid || isView || hasPlatformIssues;
    // A draft publishes nothing, so a TikTok rule that only governs scheduling must not stop the
    // user parking their work. The server draws the same line.
    const scheduleDisabled = saveBlocked || blockingTikTokIssues.length > 0;
    const draftDisabled = saveBlocked || draftBlockingIssues.length > 0;
    const savesAsDraft = status => status === CAMPAIGN_STATUS.DRAFT;
    const actionFor = (text, status) => ({
        text,
        onClick: () => onSaveCampaign(status),
        disabled: savesAsDraft(status) ? draftDisabled : scheduleDisabled,
        tooltip: (() => {
            const blocker = firstBlocker(savesAsDraft(status) ? draftBlockingIssues : blockingTikTokIssues);
            return blocker ? t(blocker.key, blocker.options) : undefined;
        })(),
    });

    const primaryStatus = modal.type === ModalType.Create ? CAMPAIGN_STATUS.SCHEDULED : selectedSocialPost?.status;
    const secondaryStatus =
        selectedSocialPost?.status === CAMPAIGN_STATUS.DRAFT ? CAMPAIGN_STATUS.SCHEDULED : CAMPAIGN_STATUS.DRAFT;

    const onSaveCampaign = async status => {
        const values = getValues();
        setIsSubmitting(true);
        let newSocialPost;
        try {
            newSocialPost = await socialPostsService.createOrUpdateSocialPost({
                ...selectedSocialPost,
                ...values,
                companies: values.companies.map(c => c.id),
                scheduledTime: new Date(values.scheduledTime).toISOString(),
                button: googleIsSelected ? values.button : 0,
                buttonContent: googleIsSelected ? values.buttonContent : '',
                autoAddMusic: tiktokIsSelected ? values.autoAddMusic : false,
                tikTokOptions: tiktokIsSelected
                    ? applyCreatorRestrictions(values.tikTokOptions, tiktokConstraints)
                    : null,
                // Keep every customized (still-selected) platform — never drop on empty media,
                // since [] is an explicit "no media" the server must see.
                overrides: (values.overrides || []).filter(o => values.platforms.includes(o.platform)),
                status,
                ...(modal.type === ModalType.Edit && { id: selectedSocialPost?.id }),
            });
        } catch (error) {
            handleApiError(
                error,
                modal.type === ModalType.Create ? 'alertMessages.createFail' : 'alertMessages.editFail',
            );
            return;
        } finally {
            setIsSubmitting(false);
        }

        if (modal.type === ModalType.Create) {
            refetchPosts(0);
        } else {
            const socialPosts = data.slice();
            const updatedSocialPostIndex = socialPosts.findIndex(c => c.id === selectedSocialPost?.id);
            if (updatedSocialPostIndex !== -1 && newSocialPost) {
                socialPosts[updatedSocialPostIndex] = newSocialPost;
                dispatch(socialPostsActions.setSocialPosts(socialPosts));
            }
            setSelectedSocialPost(newSocialPost);
        }
        refetchAvailablePlatforms?.();
        dispatch(
            alertActions.success(
                modal.type === ModalType.Create ? t('alertMessages.createSuccess') : t('alertMessages.editSuccess'),
            ),
        );
        onCloseModal();
    };

    const primaryAction = actionFor(
        modal.type === ModalType.Create ? t('buttons.schedulePost') : t('buttons.saveChanges'),
        primaryStatus,
    );
    const secondaryAction = actionFor(
        selectedSocialPost?.status === CAMPAIGN_STATUS.DRAFT ? t('buttons.schedulePost') : t('buttons.saveDraft'),
        secondaryStatus,
    );

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseModal}
            isDirty={isDirty}
            title={
                modal.type === ModalType.Create
                    ? t('buttons.createNewPost')
                    : isView
                      ? t('SocialPosts.viewPost')
                      : t('SocialPosts.editPost')
            }
            primaryButton={
                isView ? null : (
                    // A disabled MUI button emits no hover events, so the mandated disclosure hint
                    // hangs off a span around the button group.
                    <Tooltip
                        title={primaryAction.disabled && primaryAction.tooltip ? primaryAction.tooltip : ''}
                        placement="top"
                    >
                        <span style={{ display: 'inline-flex' }}>
                            <SplitButton
                                variant="contained"
                                disabled={saveBlocked}
                                options={[primaryAction, secondaryAction]}
                            />
                        </span>
                    </Tooltip>
                )
            }
            onAdditionalAction={canDelete && !isView ? onDelete : null}
            additionalTextButton={t('buttons.delete')}
            additionalActionDisabled={isSubmitting}
        >
            <FormWrapper>
                <Controller
                    control={control}
                    name="title"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('CampaignsPage.name')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                        />
                    )}
                />
                <TwoColumnRow>
                    <Controller
                        control={control}
                        name="platforms"
                        render={({ field, fieldState: { error }, ref }) => (
                            <MultiSelect
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('platforms', value, { shouldValidate: true, shouldDirty: true });
                                }}
                                options={platformsOptions}
                                label={t('common.platforms')}
                                fullWidth
                                countable
                                required
                                disabled={isView}
                                error={error}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="scheduledTime"
                        render={({ field, fieldState: { error } }) => (
                            <StyledTimeframePicker
                                value={[field.value]}
                                range={false}
                                label={t('common.scheduledTime')}
                                showTimeSelect
                                required
                                disabled={isView}
                                dateFormat="d MMM yyyy, HH:mm"
                                onBlurTimeSelect={field.onBlur}
                                onChangeTimeframe={value => {
                                    setValue('scheduledTime', value, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    });
                                }}
                                minDate={new Date()}
                                maxDate={null}
                                error={error}
                            />
                        )}
                    />
                </TwoColumnRow>
                {!autoSelectSingleLocation && (
                    <Controller
                        control={control}
                        name="companies"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Autocomplete
                                {...field}
                                ref={ref}
                                label={t('common.locations')}
                                options={locationsOptions}
                                value={field.value}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                                onChangeAutoComplete={items => {
                                    setValue('companies', items.length ? items : [], {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                groupMultiProps={{
                                    getOptionLabel: option => option.label,
                                }}
                                required
                                error={error}
                                disabled={isView}
                            />
                        )}
                    />
                )}
                <ContentTabs
                    disabled={isView}
                    platforms={platforms}
                    message={message}
                    media={measuredMedia}
                    messageError={errors.message}
                    mediaError={errors.media}
                    onMessageChange={text => setValue('message', text, { shouldDirty: true, shouldValidate: true })}
                    onMediaChange={updatedMedia =>
                        setValue('media', updatedMedia, { shouldDirty: true, shouldValidate: true })
                    }
                    button={button}
                    buttonContent={buttonContent}
                    buttonOptions={buttonOptions}
                    buttonContentError={errors.buttonContent}
                    onButtonChange={value => {
                        setValue('button', value, { shouldDirty: true, shouldValidate: true });
                        if (value === 0 || value === 6) {
                            setValue('buttonContent', '', { shouldDirty: true, shouldValidate: true });
                        }
                    }}
                    onButtonContentChange={value =>
                        setValue('buttonContent', value, { shouldDirty: true, shouldValidate: true })
                    }
                    overrides={measuredOverrides}
                    onOverridesChange={next => {
                        setValue('overrides', next, { shouldDirty: true });
                        trigger(['overrides', 'message']);
                    }}
                    isUploading={isMediaUploading}
                    setIsUploading={setIsMediaUploading}
                    selectedCompanies={selectedCompanies}
                    isMeasuringVideos={isMeasuringVideos}
                    tiktok={
                        tiktokIsSelected
                            ? {
                                  options: tikTokOptions,
                                  onChange: next => setValue('tikTokOptions', next, { shouldDirty: true }),
                                  constraints: tiktokConstraints,
                                  creatorInfo: tiktokCreatorInfo,
                                  issues: tiktokIssues,
                                  autoAddMusic,
                                  onAutoAddMusicChange: value => setValue('autoAddMusic', value, { shouldDirty: true }),
                              }
                            : null
                    }
                />
            </FormWrapper>
            <PreviewWrapper>
                <PostPreview
                    platforms={platforms}
                    companies={previewCompanies}
                    message={message}
                    media={media}
                    button={button}
                    scheduledTime={scheduledTime}
                    overrides={overrides}
                    skipEligibility={isView}
                    tiktokCreators={tiktokCreatorInfo.creators}
                />
            </PreviewWrapper>
        </Modal>
    );
};

export default EditModal;
